import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../../config/api";
import { fetchMovies, handleGuess } from "./GameEngine";
import { saveScore } from "./ScoreManager";
import useGameControls from "./GameControls";
import getUserInfo from "../../../utilities/decodeJwt";

export default function GamePage() {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [score, setScore] = useState(0);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [wrongGuessRounds, setWrongGuessRounds] = useState([]);
  const [message, setMessage] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameMode, setGameMode] = useState("normal"); // "normal", "hard", "daily", etc.
  const [hasMadeChoice, setHasMadeChoice] = useState(false);
  const [currentTime, setCurrentTime] = useState(null);
  const [maxTime, setMaxTime] = useState(200); // max time = seconds * 20
  const [hasPlayedToday, setHasPlayedToday] = useState(false);

  const timerIdRef = useRef(null);
  const [level, setLevel] = useState(1);

  const getTodayDate = () => new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  const todayDate = getTodayDate();

  const startTimer = (duration, callback) => {
    if (timerIdRef.current) clearInterval(timerIdRef.current);

    duration = Math.max(duration - (level * 3), 50); // minimum time of 2.5 seconds
    setCurrentTime(duration);
    setMaxTime(duration);

    timerIdRef.current = setInterval(() => {
      setCurrentTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerIdRef.current);
          timerIdRef.current = null;
          callback();
          return 0;
        }
        return prev - 1;
      });
    }, 50);
  };

 
  const fetchDailyMovies = async () => {
  try {
    const data = await fetchMovies("daily", level);
    setMovies(data);
    setHasMadeChoice(false);
    setMessage("");
    // ⛔ Do not auto-start timer here!
  } catch (err) {
    console.error("Failed to fetch daily movies:", err);
  }
};


  const fetchNextMovies = async (forcedLevel = level) => {
    try {
      let newMovies;
      if (gameMode !== "daily") {
        newMovies = await fetchMovies(gameMode);
      } else {
        newMovies = await fetchMovies("daily", forcedLevel);
      }
      startTimer(maxTime, () => {
          setGameOver(true);

    
          const nextLevel = level + 1;
          setLevel(nextLevel);
          setWrongGuessRounds(prev => [...prev, 0]);
          saveScore(score, gameMode);

        });
      setMovies(newMovies);
      setMessage("");
      setHasMadeChoice(false);
    } catch (error) {
      console.error("Error fetching movies:", error);
    }
  };


  const onGuess = (index) => {
  if (gameOver || movies.length < 2 || hasMadeChoice) return;
  // Start timer on first guess only for daily mode
  if (gameMode === "daily" && !currentTime) {
    startTimer(1000, () => {
          setGameOver(true);
          saveScore(score, gameMode);
        });
    const nextLevel = level + 1;
    setLevel(nextLevel); // will update eventually

    handleGuess({
      selectedIndex: index,
      level,
      movies,
      score,
      wrongGuesses,
      wrongGuessRounds,
      setScore,
      setWrongGuesses,
      setWrongGuessRounds,
      setGameOver,
          saveScore: (finalScore) => saveScore(finalScore, gameMode),
      setMessage,
      setHasMadeChoice,
      fetchNextMovies: () => fetchNextMovies(nextLevel), // explicitly pass nextLevel
    });
  }

  const nextLevel = level + 1;
  setLevel(nextLevel);

  handleGuess({
    selectedIndex: index,
    movies,
    score,
    wrongGuesses,
    wrongGuessRounds,
    setScore,
    setWrongGuesses,
    setWrongGuessRounds,
    setGameOver,
    saveScore: (finalScore) => saveScore(finalScore, gameMode),
    setMessage,
    setHasMadeChoice,
    fetchNextMovies: () => fetchNextMovies(nextLevel),
  });
};


  const startGame = () => {
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }

    setGameStarted(true);
    setScore(0);
    setWrongGuesses(0);
    setGameOver(false);
    setMessage("");
  };


  useEffect(() => {
  const checkIfPlayedToday = async () => {
    try {
      const userInfo = getUserInfo();
      if (!userInfo?.id) {
        setHasPlayedToday(false);
        return;
      }

      const res = await axios.get(`${API_BASE}/api/dailyLeaderboard/${todayDate}`);
      const players = res.data.players || [];
      const hasPlayed = players.some((player) => player.userId === userInfo.id);
      setHasPlayedToday(hasPlayed);
    } catch (err) {
      console.error("Error checking daily run status:", err);
    }
  };

  checkIfPlayedToday();
}, [todayDate]);

  useEffect(() => {
    if (!gameStarted) return;
    if (gameMode !== "daily") fetchNextMovies();
    else fetchDailyMovies();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only when starting a session
  }, [gameStarted]);

  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (gameStarted && gameOver) {
      navigate(`/leaderboards?mode=${encodeURIComponent(gameMode)}`);
    }
  }, [gameStarted, gameOver, gameMode, navigate]);

  useEffect(() => {
    if (gameOver && timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  }, [gameOver]);

  useGameControls(gameStarted && !gameOver && !hasMadeChoice, () => onGuess(0), () => onGuess(1));

  const renderCountdownBar = () => {
    if (!currentTime || !maxTime) return null;
    const percent = ((currentTime - 1) / maxTime) * 107;
    const barColor =
      percent < 20 ? "bg-rose-500" : percent < 50 ? "bg-sky-500" : "bg-emerald-500";
    return (
      <div className="mb-8 flex w-full justify-center">
        <div className="relative h-2 w-full max-w-xl overflow-hidden rounded-full bg-cinema-800 ring-1 ring-slate-700/50">
          <div
            className={`h-full rounded-full transition-all duration-100 ease-linear ${barColor}`}
            style={{
              width: `${percent}%`,
              marginLeft: `${(100 - percent) / 2}%`,
            }}
          />
        </div>
      </div>
    );
  };

return (
  <div className="mv-page relative flex flex-col items-center justify-center pb-28">
    {gameStarted ? (
      <>
        {message && (
          <div
            className={`pointer-events-none absolute left-1/2 top-1/2 z-50 max-w-[min(90vw,28rem)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border px-8 py-5 text-center font-display text-2xl font-semibold shadow-panel backdrop-blur-md ${
              message === "Correct!"
                ? "border-emerald-500/40 bg-emerald-950/90 text-emerald-300"
                : "border-rose-500/40 bg-rose-950/90 text-rose-200"
            }`}
          >
            {message}
          </div>
        )}

        <h1 className="mv-heading mb-10 max-w-3xl px-4 text-center text-2xl md:text-3xl lg:text-4xl">
          Which title has the higher IMDb score?
        </h1>

        {gameOver ? (
          <div className="mv-card max-w-lg p-10 text-center">
            <h2 className="mv-heading text-2xl md:text-3xl">Round complete</h2>
            <p className="mt-5 text-base text-slate-400">
              Taking you to the leaderboard…
            </p>
            <button
              type="button"
              onClick={() => navigate(`/leaderboards?mode=${encodeURIComponent(gameMode)}`)}
              className="mv-btn-primary mt-8 w-full sm:w-auto"
            >
              Open leaderboard
            </button>
          </div>
        ) : (
          <>
            <div className="grid w-full max-w-6xl grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
              {movies.map((movie, index) => (
                <article
                  key={movie._id}
                  className="mv-card flex flex-col overflow-hidden p-5 md:p-6"
                >
                  <div className="relative mb-4 overflow-hidden rounded-xl bg-cinema-950 ring-1 ring-slate-700/40">
                    <img
                      src={
                        movie.poster_path
                          ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
                          : "/default-poster.jpg"
                      }
                      alt={movie.title}
                      className="mx-auto max-h-[min(380px,50vh)] w-full object-contain"
                    />
                  </div>
                  <h2 className="mv-heading mb-3 text-xl leading-snug md:text-2xl">
                    {movie.title}
                  </h2>
                  <dl className="mb-4 space-y-2 text-sm text-slate-400">
                    <div className="flex flex-wrap gap-x-2">
                      <dt className="font-medium text-slate-500">Release</dt>
                      <dd>{movie.release_date}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-500">Genres</dt>
                      <dd className="text-slate-300">{movie.genre_names.join(", ")}</dd>
                    </div>
                    <div>
                      <dt className="font-medium text-slate-500">Cast</dt>
                      <dd className="leading-relaxed text-slate-300">
                        {movie.actors.join(", ")}
                      </dd>
                    </div>
                  </dl>
                  <p className="mb-6 line-clamp-4 flex-1 text-sm leading-relaxed text-slate-400 md:line-clamp-5">
                    {movie.overview}
                  </p>
                  <button
                    type="button"
                    onClick={() => onGuess(index)}
                    className="mv-btn-primary mt-auto w-full md:text-base"
                    disabled={hasMadeChoice}
                  >
                    This one is higher
                  </button>
                </article>
              ))}
            </div>

            {renderCountdownBar()}

            <div className="fixed bottom-0 left-0 right-0 border-t border-slate-700/80 bg-cinema-950/90 px-6 py-4 shadow-panel backdrop-blur-md">
              <div className="mx-auto flex max-w-2xl justify-center gap-12 font-display text-base font-medium text-slate-200 md:text-lg">
                <span>
                  Score{" "}
                  <span className="text-cinema-accent">{score}</span>
                </span>
                <span>
                  Strikes{" "}
                  <span className={wrongGuesses >= 2 ? "text-rose-400" : ""}>
                    {wrongGuesses}
                  </span>
                  <span className="text-slate-500">/3</span>
                </span>
              </div>
            </div>
          </>
        )}
      </>
    ) : (
      <div className="mv-card w-full max-w-2xl p-8 text-center md:p-12">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-cinema-accent-muted">
          MovieDuel
        </p>
        <h1 className="mv-heading text-3xl md:text-4xl">Pick the higher rating</h1>
        <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-slate-400">
          Two films, one question: which has the better IMDb score? You have three wrong
          guesses before the run ends.
        </p>

        <div className="mt-10 flex flex-col items-stretch gap-8 sm:items-center">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
            <button
              type="button"
              onClick={() => {
                setGameMode("normal");
                startGame();
              }}
              className="mv-btn-primary min-w-[200px] px-8 py-3"
            >
              Normal run
            </button>
            <button
              type="button"
              onClick={() => {
                setGameMode("hard");
                startGame();
              }}
              className="mv-btn-primary min-w-[200px] px-8 py-3"
            >
              Hard run
            </button>
            <button
              type="button"
              onClick={() => {
                if (!hasPlayedToday) {
                  setGameMode("daily");
                  startGame();
                }
              }}
              className={`mv-btn min-w-[200px] border px-8 py-3 font-semibold ${
                hasPlayedToday
                  ? "cursor-not-allowed border-slate-700 bg-cinema-850/40 text-slate-500"
                  : "border-cinema-teal/45 bg-teal-950/30 text-teal-200 hover:border-cinema-teal/70 hover:bg-teal-950/50"
              }`}
              disabled={hasPlayedToday}
            >
              {hasPlayedToday ? "Daily run complete" : "Daily run"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigate("/leaderboards")}
            className="mv-btn-secondary px-10 py-3"
          >
            View leaderboards
          </button>
        </div>
      </div>
    )}
  </div>
);
}